import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, gql, useLazyQuery } from '@apollo/client';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import GoogleLoginButton from '../components/GoogleLoginButton';

const REGISTER_MUTATION = gql`
  mutation Register($input: RegisterInput!) {
    register(input: $input) {
      success
      message
      token
      user {
        id
        username
        email
        isSemiAdmin
        isVerified
      }
      errors {
        field
        message
      }
    }
  }
`;

const CHECK_PASSWORD_STRENGTH = gql`
  query CheckPasswordStrength($password: String!) {
    checkPasswordStrength(password: $password) {
      score
      strength
      checks {
        length
        lowercase
        uppercase
        number
        special
      }
    }
  }
`;

const Register = () => {
  const [registerMutation, { loading }] = useMutation(REGISTER_MUTATION);
  const [checkPasswordStrength] = useLazyQuery(CHECK_PASSWORD_STRENGTH);
  const { login: authLogin, setError, clearError } = useAuth();
  const navigate = useNavigate();
  const [formErrors, setFormErrors] = useState([]);
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [showEmailRegister, setShowEmailRegister] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm();

  const watchedPassword = watch('password');

  // 監聽密碼變化，檢查強度
  React.useEffect(() => {
    if (watchedPassword && watchedPassword.length > 0) {
      const checkStrength = async () => {
        try {
          const { data } = await checkPasswordStrength({
            variables: { password: watchedPassword }
          });
          setPasswordStrength(data.checkPasswordStrength);
        } catch (error) {
          console.error('Password strength check error:', error);
        }
      };
      
      // 防抖，避免過於頻繁的請求
      const timeoutId = setTimeout(checkStrength, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setPasswordStrength(null);
    }
  }, [watchedPassword, checkPasswordStrength]);

  const onSubmit = async (data) => {
    console.log('Register form submitted:', { username: data.username, email: data.email });
    clearError();
    setFormErrors([]);

    try {
      const response = await registerMutation({
        variables: {
          input: {
            username: data.username,
            email: data.email,
            password: data.password
          }
        }
      });

      const result = response.data.register;

      if (result.success) {
        console.log('Registration successful:', result.user.username);
        authLogin({
          user: result.user,
          token: result.token
        });
        navigate('/profile');
      } else {
        console.log('Registration failed:', result.message);
        setFormErrors(result.errors || [{ field: 'general', message: result.message }]);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('註冊過程發生錯誤，請稍後再試');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            註冊新帳號
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            或{' '}
            <Link
              to="/login"
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              登入現有帳號
            </Link>
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {/* Google 註冊按鈕 */}
          <div>
            {/* 提示文字 */}
            <div className="mb-3 space-y-1">
              <div className="flex items-center justify-center">
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                  推薦
                </span>
              </div>
              <p className="text-xs text-center text-gray-600">
                其他人不會看見你的email
              </p>
              <p className="text-xs text-center text-gray-600">
                登入後可改名
              </p>
            </div>
            <GoogleLoginButton text="使用 Google 註冊" variant="default" />
          </div>

          {/* 分隔線 - 可折疊 */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center">
              <button
                type="button"
                onClick={() => setShowEmailRegister(!showEmailRegister)}
                className="px-2 bg-gray-50 text-gray-500 text-sm hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded transition-colors"
              >
                {showEmailRegister ? '隱藏' : '其他註冊方法'}
                <svg
                  className={`inline-block ml-1 w-4 h-4 transition-transform ${showEmailRegister ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* 提示信息 - 與註冊表單一起折疊 */}
          {showEmailRegister && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-xs text-yellow-800 text-center">
                ⚠️  使用此方式註冊可能無法使用完整功能（比如發文，留言，上傳照片））
              </p>
            </div>
          )}
        </div>

        {showEmailRegister && (
          <form className="mt-6 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                名稱
              </label>
              <input
                {...register('username', {
                  required: '請輸入名稱',
                  minLength: {
                    value: 3,
                    message: '名稱至少需要3個字符'
                  },
                  maxLength: {
                    value: 30,
                    message: '名稱不能超過30個字符'
                  }
                })}
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="請輸入名稱"
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                email
              </label>
              <input
                {...register('email', {
                  required: '請輸入email',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: '請輸入有效的email'
                  }
                })}
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="請輸入email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                密碼
              </label>
              <input
                {...register('password', {
                  required: '請輸入密碼',
                  minLength: {
                    value: 6,
                    message: '密碼至少需要6個字符'
                  }
                })}
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="請輸入密碼"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
              
              {passwordStrength && (
                <PasswordStrengthIndicator
                  strength={passwordStrength.strength}
                  checks={passwordStrength.checks}
                />
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                確認密碼
              </label>
              <input
                {...register('confirmPassword', {
                  required: '請確認密碼',
                  validate: (value) => value === watchedPassword || '密碼不一致'
                })}
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="請再次輸入密碼"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          {formErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              {formErrors.map((error, index) => (
                <p key={index} className="text-sm text-red-600">
                  {error.message}
                </p>
              ))}
            </div>
          )}

          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              className="w-full"
            >
              {loading ? '註冊中...' : '註冊'}
            </Button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default Register; 